import React, { useMemo, useState } from 'react';
import { Search, Home, X } from 'lucide-react';
import { Input } from '../ui/input';
import Flag from './Flag';
import { useJazyk } from '../../context/LanguageContext';

/**
 * Výběr domovské země při prvním otevření Bloom World.
 *
 * Není to formulář, který by šel jen odklikat – dá se vybrat i z mapy nad ním.
 * Přeskočit jde vždycky a nastavení se dá kdykoli změnit; domovská země je jen
 * předvolba obsahu, ne něco, co by se o člověku někam ukládalo veřejně.
 */
export default function HomeCountryPicker({ zeme, onVyber, onPreskocit, uklada }) {
  const { t } = useJazyk();
  const [hledani, setHledani] = useState('');

  const nabidka = useMemo(() => {
    const h = hledani.trim().toLowerCase();
    const bezDiakritiky = (t) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const hn = bezDiakritiky(h);
    const filtrovane = h
      ? zeme.filter((z) => bezDiakritiky(z.name_cs).includes(hn)
          || bezDiakritiky(z.name_en).includes(hn)
          || z.iso2.toLowerCase() === hn)
      : // Bez hledání ukážeme nejdřív země, kde Bloom něco má – tam bude
        // většina lidí, a ostatní si zemi najdou hledáním.
        [...zeme].sort((a, b) => (b.total - a.total) || a.name_cs.localeCompare(b.name_cs, 'cs'));
    return filtrovane.slice(0, 12);
  }, [zeme, hledani]);

  return (
    <div className="bg-white border border-bloom-violet/20 rounded-3xl p-5 sm:p-6 shadow-sm"
         data-testid="home-country-picker">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg sm:text-xl font-bold text-bloom-text flex items-center gap-2">
            <Home className="w-5 h-5 text-bloom-violet" />
            {t('domov.otazka')}
          </h2>
          <p className="mt-1.5 text-sm text-bloom-sub max-w-xl">
            {t('domov.popis')}
          </p>
        </div>
        <button type="button" onClick={onPreskocit}
                className="text-sm text-bloom-sub hover:text-bloom-text shrink-0 inline-flex items-center gap-1"
                data-testid="skip-home-country">
          <X className="w-3.5 h-3.5" />{t('domov.preskocit')}
        </button>
      </div>

      <div className="relative mt-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-bloom-sub" />
        <Input value={hledani} onChange={(e) => setHledani(e.target.value)}
               placeholder={t('domov.najitZemi')} className="pl-9" aria-label={t('domov.najitZemi')}
               data-testid="home-country-search" />
      </div>

      <ul className="mt-3 grid gap-2 sm:grid-cols-3">
        {nabidka.map((z) => (
          <li key={z.iso2}>
            <button type="button" disabled={uklada} onClick={() => onVyber(z.iso2)}
                    className="w-full flex items-center gap-2.5 text-left bg-bloom-bg border border-border/40 rounded-xl px-3 py-2.5 hover:border-bloom-violet/40 hover:bg-white transition-all disabled:opacity-50"
                    data-testid={`pick-home-${z.iso2}`}>
              <Flag emoji={z.flag} iso2={z.iso2} size="sm" />
              <span className="text-sm text-bloom-text truncate flex-1">{z.name_cs}</span>
            </button>
          </li>
        ))}
      </ul>

      {nabidka.length === 0 && (
        <p className="mt-3 text-sm text-bloom-sub">{t('domov.nicNeodpovida')}</p>
      )}

      <p className="mt-3 text-xs text-bloom-sub/70">
        {t('domov.klikniDoMapy')}
      </p>
    </div>
  );
}
